import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Spinner, cn } from '@klaro7/ui';

import { useWallet, useRechargePackages, usePromoEligibility } from '@/features/wallet/hooks/useWallet';
import { hasCompletedFirstRecharge, RECOMMENDED_RECHARGE_AMOUNT } from '@/features/wallet/lib/firstRechargePromo';
import { useFavorites } from '@/features/favorites/hooks/useFavorites';
import { ConsultantCard } from '@/features/professionals/components/ConsultantCard';
import { FeaturedCard } from '@/features/professionals/components/FeaturedCard';
import { SpecializationCard } from '@/features/professionals/components/SpecializationsPanel';
import {
  FilterSidebar,
  FilterMobileToggle,
  type FilterValues,
} from '@/features/professionals/components/FilterSidebar';
import { HeroBanner } from './HeroBanner';
import { SocialLinks } from '@/components/SocialLinks';
import {
  useFeaturedProfessionals,
  usePublicProfessionals,
  useSpecializations,
} from '@/features/professionals/hooks/usePublicProfessionals';
import { resolveUploadUrl } from '@/lib/uploads';
import type { PublicProfessionalQueryParams } from '@/features/professionals/services/professional-public.service';

export function RegisteredHomePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [featuredCollapsed, setFeaturedCollapsed] = useState(false);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const favScrollRef = useRef<HTMLDivElement>(null);

  // Pin every featured card (and the spec card) to the first card's natural
  // height; taller content scrolls inside. Mirrors PromoHomePage so all cards
  // in the Top 7 grid — including the Specializzazioni card — stay equal-height.
  const gridRef = useRef<HTMLDivElement>(null);
  const [cardHeight, setCardHeight] = useState<number | undefined>();
  // Pin heights only on multi-column layouts (sm+). On a single mobile column
  // pinning just adds dead vertical space, so let the cards size naturally.
  const [pinHeights, setPinHeights] = useState(false);

  // Data hooks
  const { data: wallet, isLoading: walletLoading } = useWallet();
  const { isPromoEligible } = usePromoEligibility();
  const hasRecharged = hasCompletedFirstRecharge(wallet);
  const showPromo = !walletLoading && isPromoEligible;
  const { data: favorites, isLoading: favoritesLoading } = useFavorites();
  const { data: packagesData } = useRechargePackages();

  // ── Directory filters from URL ──
  const specialization = searchParams.get('specialization') ?? undefined;
  const search = searchParams.get('search') ?? undefined;
  const minRate = searchParams.get('minRate') ? parseFloat(searchParams.get('minRate')!) : undefined;
  const maxRate = searchParams.get('maxRate') ? parseFloat(searchParams.get('maxRate')!) : undefined;
  const available = searchParams.get('available') === 'true' || undefined;
  const sortByRaw = searchParams.get('sortBy') ?? undefined;
  const page = searchParams.get('page') ? parseInt(searchParams.get('page')!, 10) : 1;

  let sortBy: PublicProfessionalQueryParams['sortBy'];
  let sortOrder: 'asc' | 'desc' | undefined;
  if (sortByRaw) {
    const [s, o] = sortByRaw.split('-') as [string, string];
    sortBy = s as PublicProfessionalQueryParams['sortBy'];
    sortOrder = o as 'asc' | 'desc';
  }

  const queryParams: PublicProfessionalQueryParams = {
    page,
    limit: 12,
    specialization,
    search,
    minRate,
    maxRate,
    available,
    sortBy,
    sortOrder,
  };

  const { data: featured, isLoading: featuredLoading } = useFeaturedProfessionals();
  const { data: specializations } = useSpecializations();
  const { data: professionalsData, isLoading: professionalsLoading } =
    usePublicProfessionals(queryParams);

  const professionals = professionalsData?.data ?? [];
  const pagination = professionalsData?.pagination;

  // On pagination change, scroll the directory back to the top so the user
  // starts from the first consultant of the new page. Skip the initial mount.
  const dirDidMountRef = useRef(false);
  const dirTopRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!dirDidMountRef.current) {
      dirDidMountRef.current = true;
      return;
    }
    dirTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [page]);

  useLayoutEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)');
    const sync = () => setPinHeights(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  useLayoutEffect(() => {
    const first = gridRef.current?.firstElementChild as HTMLElement | undefined;
    if (!first) return;
    const measure = () => setCardHeight(first.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(first);
    return () => ro.disconnect();
  }, [featured, featuredCollapsed]);

  const updateSearchParams = (updates: Record<string, string | undefined>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(updates)) {
      if (value == null || value === '') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    }
    // Reset page when filters change (unless explicitly updating page)
    if (!('page' in updates)) {
      next.delete('page');
    }
    setSearchParams(next, { replace: true });
  };

  const filterValues: FilterValues = {
    specialization,
    minRate,
    maxRate,
    available,
  };

  const handleFilterChange = (filters: FilterValues) => {
    updateSearchParams({
      specialization: filters.specialization,
      minRate: filters.minRate != null ? String(filters.minRate) : undefined,
      maxRate: filters.maxRate != null ? String(filters.maxRate) : undefined,
      available: filters.available ? 'true' : undefined,
    });
  };

  const handleFilterReset = () => {
    setSearchParams({}, { replace: true });
  };

  const packages = packagesData?.packages ?? [];

  return (
    <div className="text-brand-navy">
      {/* ── 1. Bonus Balance Highlight ── */}
      <section className="px-4 py-6">
        <div className="mx-auto max-w-6xl">
          <div className="rounded-xl border border-brand-orange/20 bg-white px-6 py-5 shadow-sm">
            {walletLoading ? (
              <div className="flex justify-center py-4">
                <Spinner size="md" className="border-brand-orange/30 border-t-brand-orange" />
              </div>
            ) : wallet && wallet.bonusBalance > 0 ? (
              <div className="flex flex-col items-center gap-1 text-center sm:flex-row sm:gap-4 sm:text-left">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-orange/10">
                  <svg className="h-6 w-6 text-brand-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-neutral-500">Il tuo Bonus</p>
                  <p className="text-3xl font-bold text-brand-orange">
                    {wallet.bonusBalance.toFixed(2)}&euro;
                  </p>
                  <p className="text-xs text-neutral-400">Saldo bonus disponibile</p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1 text-center sm:flex-row sm:gap-4 sm:text-left">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-neutral-100">
                  <svg className="h-6 w-6 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-neutral-500">Il tuo Bonus</p>
                  <p className="text-sm text-neutral-400">Nessun bonus attivo</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── 2. Bonus 7 Minuti (conditional) ── */}
      {wallet && !wallet.freeMinutesUsed && !hasRecharged && (
        <section className="px-4 pb-6">
          <div className="mx-auto max-w-6xl">
            <div className="flex flex-col gap-3 rounded-xl border border-brand-orange/20 bg-brand-orange/[0.06] px-5 py-4 text-center sm:flex-row sm:items-center sm:justify-between sm:text-left">
              <div>
                <p className="text-sm font-semibold text-brand-orange">
                  Hai 7 minuti di consulenza gratuita!
                </p>
                <p className="text-xs text-neutral-500">
                  Riscatta i tuoi minuti gratis e prova subito un consulente
                </p>
              </div>
              <Link
                to="/my/wallet"
                className="inline-flex items-center justify-center rounded-lg bg-brand-orange px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-orange/90 min-h-[44px] sm:shrink-0"
              >
                Riscatta Ora
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ── 3. I Tuoi Preferiti ── */}
      <section className="px-4 pb-6">
        <div className="mx-auto max-w-6xl">
          <h2 className="mb-3 text-lg font-bold text-brand-navy">I Tuoi Preferiti</h2>

          {favoritesLoading ? (
            <div className="flex justify-center py-6">
              <Spinner size="md" className="border-brand-teal/30 border-t-brand-teal" />
            </div>
          ) : favorites && favorites.length > 0 ? (
            <div
              ref={favScrollRef}
              className="-mx-4 flex gap-3 overflow-x-auto px-4 pb-2 scrollbar-thin scrollbar-thumb-neutral-200 scrollbar-track-transparent"
            >
              {favorites.filter((item) => item.professional).map((item) => (
                <Link
                  key={item.id}
                  to={`/consultant/${item.professionalId}`}
                  className="flex w-32 shrink-0 flex-col items-center gap-2 rounded-xl border border-neutral-200 bg-white px-3 py-4 shadow-sm transition-all hover:border-brand-teal/30 hover:shadow-md"
                >
                  {item.professional?.avatar ? (
                    <img
                      src={resolveUploadUrl(item.professional.avatar)}
                      alt={`${item.professional?.firstName ?? ''} ${item.professional?.lastName ?? ''}`}
                      className="h-14 w-14 rounded-full border border-neutral-200 object-cover"
                    />
                  ) : (
                    <div className="flex h-14 w-14 items-center justify-center rounded-full border border-neutral-200 bg-neutral-100">
                      <img src="/images/logo-navy.png" alt="" className="h-8 w-8 object-contain opacity-60" />
                    </div>
                  )}
                  <div className="w-full text-center">
                    <p className="truncate text-xs font-semibold text-brand-navy">
                      {item.professional?.firstName} {item.professional?.lastName}
                    </p>
                    {item.professional?.title && (
                      <p className="truncate text-[10px] text-neutral-400">
                        {item.professional.title}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-neutral-200 bg-white/60 px-5 py-6 text-center">
              <p className="text-sm text-neutral-400">
                Non hai ancora aggiunto consulenti ai preferiti
              </p>
              {/* La directory è in fondo a questa stessa pagina: si scrolla, non si naviga
                  (l'ancora "/#directory" non corrispondeva ad alcun id e non faceva nulla). */}
              <button
                type="button"
                onClick={() => dirTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="mt-2 inline-block text-sm font-medium text-brand-teal underline underline-offset-2 hover:text-brand-teal-400"
              >
                Sfoglia i consulenti
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── 4. Ricariche con Bonus ── */}
      {packages.length > 0 && !hasRecharged && (
        <section className="px-4 pb-6">
          <div className="mx-auto max-w-6xl">
            <h2 className="mb-3 text-lg font-bold text-brand-navy">Scegli una delle ricariche bonus</h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {packages.map((pkg) => {
                const isRecommended = pkg.amount === RECOMMENDED_RECHARGE_AMOUNT;
                return (
                  <div
                    key={pkg.amount}
                    className={cn(
                      'relative flex flex-col items-center gap-2 rounded-xl border bg-white px-5 py-5 shadow-sm',
                      isRecommended
                        ? 'border-2 border-brand-orange shadow-md ring-1 ring-brand-orange/20 sm:-translate-y-1'
                        : 'border-neutral-200',
                    )}
                  >
                    {isRecommended && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-brand-orange px-3 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white shadow-sm">
                        Più conveniente
                      </span>
                    )}
                    <p className="text-sm font-medium text-neutral-500">
                      Ricarica &euro;{pkg.amount}
                    </p>
                    <p className="text-2xl font-bold text-brand-navy">
                      Ottieni &euro;{pkg.totalCredit}
                    </p>
                    <span
                      className={cn(
                        'rounded-full px-3 py-0.5 text-xs font-semibold',
                        isRecommended
                          ? 'bg-brand-orange/15 text-brand-orange'
                          : 'bg-emerald-100 text-emerald-700',
                      )}
                    >
                      +&euro;{pkg.bonus} bonus
                    </span>
                    <Link
                      to="/my/wallet"
                      className="mt-2 inline-flex w-full items-center justify-center rounded-lg bg-brand-orange px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-orange/90 min-h-[44px]"
                    >
                      Ricarica
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── 5. Hero Banner ── */}
      <HeroBanner />

      {/* ── 6. Top 7 della Settimana ── */}
      <section className="px-4 pb-6">
        <div className="mx-auto max-w-6xl">
          <button
            onClick={() => setFeaturedCollapsed(!featuredCollapsed)}
            className="mb-4 flex w-full items-center gap-3"
          >
            <div className="h-px flex-1 bg-gradient-to-r from-brand-orange/30 to-transparent" />
            <h2 className="shrink-0 text-sm font-semibold uppercase tracking-widest text-brand-orange/70">
              Top 7 della Settimana
            </h2>
            <svg
              className={`h-4 w-4 shrink-0 text-brand-orange/50 transition-transform ${featuredCollapsed ? '' : 'rotate-180'}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
            <div className="h-px flex-1 bg-gradient-to-l from-brand-orange/30 to-transparent" />
          </button>

          {!featuredCollapsed && (
            <>
              {featuredLoading ? (
                <div className="flex justify-center py-8">
                  <Spinner size="lg" className="border-brand-orange/30 border-t-brand-orange" />
                </div>
              ) : featured && featured.length > 0 ? (
                <div className="space-y-6">
                  <div ref={gridRef} className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    {featured.slice(0, 4).map((pro, i) => (
                      <FeaturedCard
                        key={pro.id}
                        professional={pro}
                        rank={i + 1}
                        showPromoPrice={showPromo}
                        height={pinHeights && i !== 0 ? cardHeight : undefined}
                      />
                    ))}
                    {featured.length <= 4 && specializations && specializations.length > 0 && (
                      <SpecializationCard
                        specializations={specializations}
                        active={specialization}
                        onSelect={(name) => updateSearchParams({ specialization: name })}
                        height={pinHeights ? cardHeight : undefined}
                      />
                    )}
                  </div>
                  {featured.length > 4 && (
                    <>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                        {featured.slice(4).map((pro, i) => (
                          <FeaturedCard
                            key={pro.id}
                            professional={pro}
                            rank={i + 5}
                            showPromoPrice={showPromo}
                            height={pinHeights ? cardHeight : undefined}
                          />
                        ))}
                        {specializations && specializations.length > 0 && (
                          <SpecializationCard
                            specializations={specializations}
                            active={specialization}
                            onSelect={(name) => updateSearchParams({ specialization: name })}
                            height={pinHeights ? cardHeight : undefined}
                          />
                        )}
                      </div>
                      <div className="relative flex items-center gap-4">
                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-brand-teal/30 to-brand-teal/30" />
                        <span className="shrink-0 text-xs font-semibold uppercase tracking-[0.18em] text-brand-teal/70">
                          Anche da scoprire
                        </span>
                        <div className="h-px flex-1 bg-gradient-to-l from-transparent via-brand-teal/30 to-brand-teal/30" />
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <p className="py-6 text-center text-sm text-neutral-400">
                  Nessun consulente in evidenza al momento
                </p>
              )}
            </>
          )}
        </div>
      </section>

      {/* ── 7. Directory: Consultant Listing + FilterSidebar ── */}
      <section ref={dirTopRef} className="px-4 pb-16">
        <div className="mx-auto max-w-6xl lg:flex lg:gap-6">
          {/* Main content */}
          <div className="min-w-0 flex-1">
            {/* Header + sort */}
            <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <FilterMobileToggle
                  filters={filterValues}
                  onClick={() => setMobileFilterOpen(true)}
                />
                <h2 className="text-xl font-bold text-brand-navy">
                  {specialization
                    ? `Consulenti: ${specialization}`
                    : search
                      ? `Risultati per "${search}"`
                      : 'Tutti i Consulenti'}
                  {pagination && (
                    <span className="ml-2 text-sm font-normal text-neutral-400">
                      ({pagination.total})
                    </span>
                  )}
                </h2>
              </div>

              <select
                value={sortByRaw ?? ''}
                onChange={(e) => {
                  updateSearchParams({ sortBy: e.target.value || undefined });
                }}
                className="h-11 rounded-lg border border-neutral-200 bg-white px-3 text-sm text-brand-navy focus:border-brand-teal/50 focus:outline-none"
              >
                <option value="">Ordina per...</option>
                <option value="rating-desc">Rating</option>
                <option value="price-asc">Prezzo crescente</option>
                <option value="price-desc">Prezzo decrescente</option>
                <option value="reviews-desc">Popolarita</option>
              </select>
            </div>

            {/* Consultant list */}
            {professionalsLoading ? (
              <div className="flex justify-center py-12">
                <Spinner size="lg" className="border-brand-teal/30 border-t-brand-teal" />
              </div>
            ) : professionals.length > 0 ? (
              <div className="flex flex-col gap-3">
                {professionals.map((pro) => (
                  <ConsultantCard key={pro.id} professional={pro} showPromoPrice={showPromo} />
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-neutral-200 bg-white/60 py-16 text-center">
                <p className="text-neutral-400">Nessun consulente trovato</p>
                {(specialization || search || available) && (
                  <button
                    onClick={handleFilterReset}
                    className="mt-3 text-sm text-brand-teal underline underline-offset-2 hover:text-brand-teal-400"
                  >
                    Cancella tutti i filtri
                  </button>
                )}
              </div>
            )}

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <button
                  disabled={!pagination.hasPrevPage}
                  onClick={() => updateSearchParams({ page: String(page - 1) })}
                  className="rounded-lg border border-neutral-200 bg-white px-4 py-2.5 text-sm text-neutral-600 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-30 min-h-[44px]"
                >
                  Precedente
                </button>
                <span className="px-4 text-sm text-neutral-400">
                  {pagination.page} / {pagination.totalPages}
                </span>
                <button
                  disabled={!pagination.hasNextPage}
                  onClick={() => updateSearchParams({ page: String(page + 1) })}
                  className="rounded-lg border border-neutral-200 bg-white px-4 py-2.5 text-sm text-neutral-600 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-30 min-h-[44px]"
                >
                  Successiva
                </button>
              </div>
            )}
          </div>

          {/* Sidebar — right side */}
          <FilterSidebar
            filters={filterValues}
            onFilterChange={handleFilterChange}
            onReset={handleFilterReset}
            mobileOpen={mobileFilterOpen}
            onMobileOpenChange={setMobileFilterOpen}
          />
        </div>
      </section>

      {/* ── 8. Trust badges ── */}
      <section className="border-t border-neutral-200 px-4 py-10">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-6 md:grid-cols-4">
          {[
            { icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', label: 'Disponibili 24/7' },
            { icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z', label: 'Transazioni sicure' },
            { icon: 'M5 13l4 4L19 7', label: 'Professionisti verificati' },
            { icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z', label: 'Chat e telefono' },
          ].map(({ icon, label }) => (
            <div key={label} className="flex flex-col items-center gap-2 text-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-teal/10">
                <svg className="h-5 w-5 text-brand-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={icon} />
                </svg>
              </div>
              <span className="text-xs font-medium text-neutral-500">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-neutral-200 px-4 py-6">
        <div className="mx-auto max-w-6xl text-center">
          <SocialLinks className="mb-3 flex items-center justify-center gap-4" />
          <p className="text-xs text-neutral-400">
            Klaro7 - Consulenza di Cartomanzia Online
          </p>
        </div>
      </footer>
    </div>
  );
}
